import {
  ONBOARDING_STEP_GROUPS,
  type OnboardingStep,
  type OnboardingStepGroup,
} from '../constants/onboardingStepGroups';

type StepContext = {
  group: OnboardingStepGroup;
  groupIndex: number;
  step: OnboardingStep;
  stepIndex: number;
};

// `/step/dynamic-assessment` (the test-arm survey entry) isn't a member of any
// step group — it's routed to directly by the resolver and advances via
// `dynamicSurveyNextStep`, not the linear walk. For progress + header chrome it
// belongs to the DYNAMIC SURVEY region, so alias it to that group's first step
// (`/step/assigned-screeners`). Keeping it out of the group array leaves
// `findNextStep`'s group-boundary crossings intact.
const STEP_CONTEXT_ALIASES: Record<string, string> = {
  '/step/dynamic-assessment': '/step/assigned-screeners',
};

/** Look up a step config by pathname, normalizing a trailing slash. */
export function getStepContext(pathname: string): StepContext | null {
  const trimmed = pathname.replace(/\/$/, '') || pathname;
  const normalized = STEP_CONTEXT_ALIASES[trimmed] ?? trimmed;
  for (let groupIndex = 0; groupIndex < ONBOARDING_STEP_GROUPS.length; groupIndex += 1) {
    const group = ONBOARDING_STEP_GROUPS[groupIndex];
    const stepIndex = group.steps.findIndex((s) => s.path === normalized);
    if (stepIndex >= 0) {
      return { group, groupIndex, step: group.steps[stepIndex], stepIndex };
    }
  }
  return null;
}

/**
 * The next step in declared order: the next step in the same group, or the
 * first step of the next group (and null past the end). Linear — only
 * meaningful for the survey region. The eligibility group's steps are
 * state-branched alternatives, not a sequence, so callers must gate on being
 * in the survey region (see `goToNextStep`); `resolveOnboardingRoute` owns those
 * branches.
 */
export function findNextStep(pathname: string): string | null {
  const ctx = getStepContext(pathname);
  if (!ctx) return null;
  const { group, groupIndex, stepIndex } = ctx;
  if (stepIndex + 1 < group.steps.length) return group.steps[stepIndex + 1].path;
  return ONBOARDING_STEP_GROUPS[groupIndex + 1]?.steps[0].path ?? null;
}

/**
 * Progress fraction (0–1) for the current step, or null when the bar
 * shouldn't show. Group-indexed (matches Sparta's `calculateProgress`):
 * navigating between two steps in the same group leaves the bar untouched;
 * crossing a group boundary advances it. The last group is always 1, as is any
 * step flagged `progressComplete` (a terminal dead end like empty-program).
 */
export function calculateProgress(pathname: string): number | null {
  const ctx = getStepContext(pathname);
  if (!ctx || ctx.step.showHeader === false || ctx.step.showProgress === false) {
    return null;
  }
  if (ctx.step.progressComplete) return 1;
  const lastGroupIndex = ONBOARDING_STEP_GROUPS.length - 1;
  if (ctx.groupIndex === lastGroupIndex) return 1;
  return Math.floor((ctx.groupIndex / lastGroupIndex) * 100) / 100;
}

/**
 * Walk the visited-step history backwards (skipping the current top) and
 * return the first step that lives in the current bi-directional group.
 *
 * History-driven instead of index-driven so branch points work correctly:
 * `/company-search` leads to EITHER `/employer-or-health-plan-verification`
 * (orgs with FN_LN_DOB_EMPID) OR `/employee-zip-verification` (orgs with
 * FN_LN_DOB_AND_EMPID_OR_ZC). The two branches are siblings, not a
 * sequence, so back from either must return to `/company-search` — never
 * the *other* branch. A linear `stepIndex - 1` would walk through both.
 *
 * Mirrors Sparta's PREVIOUS_STEP reducer (sparta/src/user/reducers/registrationProgress.ts).
 */
export function findHistoryPreviousStep(
  pathname: string,
  history: readonly string[]
): OnboardingStep | null {
  const ctx = getStepContext(pathname);
  if (!ctx || ctx.group.direction !== 'bi-directional') return null;
  const groupPaths = new Set(ctx.group.steps.map((s) => s.path));

  // The top of history is (or matches) the current step; skip it. Then walk
  // backwards looking for the first step that's also in the current group.
  for (let i = history.length - 2; i >= 0; i -= 1) {
    const candidate = history[i];
    if (groupPaths.has(candidate)) {
      return ctx.group.steps.find((s) => s.path === candidate) ?? null;
    }
  }
  return null;
}

/**
 * Whether the back chevron should be shown for the current step. True when
 * either history has an in-group predecessor, OR the step is a non-first step
 * of a bi-directional group — the latter lets `goBack` fall back to the group's
 * first step on a direct deep link / refresh that lost history (e.g. landing on
 * /employer-or-health-plan-verification → back to /company-search).
 */
export function canShowBack(pathname: string, history: readonly string[]): boolean {
  const ctx = getStepContext(pathname);
  if (!ctx || ctx.step.showHeader === false || ctx.step.showBack === false) return false;
  if (findHistoryPreviousStep(pathname, history) !== null) return true;
  return ctx.group.direction === 'bi-directional' && ctx.stepIndex > 0;
}

/** Whether the persistent header chrome should render at all on this step. */
export function canShowHeader(pathname: string): boolean {
  const ctx = getStepContext(pathname);
  if (!ctx) return true;
  return ctx.step.showHeader !== false;
}
